import React, {useEffect, useState, useRef} from "react";
import {
    VsiBubble,
    VsiH4,
    VsiH5,
    VsiMedText,
    VsiSmallText
} from "../../../../styled/App.styled";
import { useVsiAppContext} from "../../../../stores/VsiApp";
import {useSearchParams} from "react-router-dom";
import {useAuthWithRoute, useDataWithRoute, useTitleFromComponent} from "../../../../utils/hooks";
import {Button, ButtonGroup, Card, Col, FormGroup, Input, Label, Row, Spinner, Toast, ToastBody, ToastHeader} from "reactstrap";
import VsiLabeledSelect from "../../../../components/VsiLabeledSelect";
import Stockchart from "../../../../components/Stockchart";
import { forecastWithRate, forecastWithRateV2, formatStockChartConfig } from "../../../../utils/Helpers";
import {genericColors} from "../../../../utils/chartUtils";
import VsiCreatableSelect from "../../../../components/VsiCreatableSelect";
import {DeleteAuth, PostAuth, PutAuth} from "../../../../utils/Verbs";
import dayjs from "dayjs";

const allTypes = [
  {
    name: "vcpu",
    label: "vCPU",
    chartDefs: [
      { name: "Virtual CPU Used", field: "total_virtual_cpus_used", grField: "cpu_used_growth" },
      { name: "Virtual CPUs Allocated", field: "total_virtual_cpus_alloc", grField: "cpu_capacity_growth" },
    ],
    yAxis: [
        {
            title: {
                text: 'MHz',
            },
            opposite: false,

        }
    ],
    colors: genericColors,
  },
  {
    name: "memory",
    label: "Memory",
    chartDefs: [
      { name: "Memory Used", field: "memory_used", grField: "memory_used_growth" },
      { name: "Memory Allocated", field: "total_memory", grField: "memory_capacity_growth" },
    ],
    yAxis: [
        {
            title: {
                text: 'GiB',
            },
            opposite: false,

        }
    ],
    colors: genericColors,
  },
  {
    name: "disk",
    label: "Disk",
    chartDefs: [
      { name: "Disk Used", field: "used", grField: "disk_used_growth" },
      { name: "Disk Allocated", field: "allocated", grField: "disk_capacity_growth" },
    ],
    yAxis: [
        {
            title: {
                text: 'GiB',
            },
            opposite: false,

        }
    ],
    colors: genericColors,
  },
//   {
//     name: "cost",
//     label: "Cost",
//     chartDefs: [
//       { name: "Estimated Utilization Cost", field: "total_cost" },
//       { name: "Cluster Cost", field: "utilization_cost" },
//     ],
//     yAxis: [
//         {
//             title: {
//                 text: '',
//             },
//             opposite: false,

//         }
//     ],
//     colors: genericColors,
//   },
];

const SidePerformanceCategories = ({theme, cloud}) => {
    return (
        <div className={"d-flex flex-row"}>
            <Toast className={`bg-${theme}`}>
                <ToastHeader className={`bg-${theme}`}>
                    <VsiMedText>
                        Subscription:
                    </VsiMedText>
                </ToastHeader>
                <ToastBody>
                    <VsiMedText>{cloud?.subscription ?? 'N/A'}</VsiMedText>
                </ToastBody>
            </Toast>
            <Toast className={`bg-${theme}`}>
                <ToastHeader className={`bg-${theme}`}>
                    <VsiMedText>
                        Geography:
                    </VsiMedText>
                </ToastHeader>
                <ToastBody>
                    <VsiMedText>{cloud?.geography ?? 'N/A'}</VsiMedText>
                </ToastBody>
            </Toast>
            <Toast className={`bg-${theme}`}>
                <ToastHeader className={`bg-${theme}`}>
                    <VsiMedText>
                        Cloud:
                    </VsiMedText>
                </ToastHeader>
                <ToastBody>
                    <VsiMedText>{cloud?.cloud_type ?? 'N/A'}</VsiMedText>
                </ToastBody>
            </Toast>
        </div>
    )
}

export default function ClusterMigrationModeler({context, title}) {
    useTitleFromComponent(`${title}`);

    const { theme, client, periodId, user, getAccessTokenSilently } = useVsiAppContext();

    const [searchParams, setSearchParams] = useSearchParams();

    const [allSavedModels, {
        isLoading: isAllSavedModelsLoading,
        reFetchData: reFetchModels
    }] = useAuthWithRoute(`/api/v6/admin/cluster_device_migration_modeling/${client.clientId}`);
    const [allSavedModelsOptions, setAllSavedModelsOptions] = useState([]);
    const [selectedModel, setSelectedModel] = useState(null);

    const [allDevicesWithClusters, {isLoading: isAllDevicesWithClustersLoading}] = useDataWithRoute(`/api/v6/report/virtual_devices_with_clusters_proc?client_id=${client.clientId}&period_id=${periodId}`, !isAllSavedModelsLoading);
    const [allDevicesWithClustersOptions, setAllDevicesWithClustersOptions] = useState([]);

    const [allResourceGroups, {isLoading: isAllResourceGroupsLoading}] = useDataWithRoute(`/api/v6/report/cloud_all_resouce_groups_proc?client_id=${client.clientId}`);
    const [allDevices, {isLoading: isAllVirtualDevicesLoading}] = useDataWithRoute(`/api/v6/report/virtual_datacenters_list_proc?client_id=${client.clientId}&period_id=${periodId}`);

    const isAllDevicesLoading = isAllResourceGroupsLoading && isAllVirtualDevicesLoading;

    const [allDevicesOptions, setAllDevicesOptions] = useState([]);

    const [currentDeviceSelected, setCurrentDeviceSelected] = useState(null);
    const [currentClusterSelected, setCurrentClusterSelected] = useState(null);

    const [allCurrentClusters, {isLoading: isAllCurrentClustersLoading}] = useDataWithRoute(`/api/v6/report/virtual_cluster_migration_modeling_list_proc?client_id=${client.clientId}&period_id=${periodId}&device_name=${encodeURI(currentDeviceSelected?.value)}`, (!isAllSavedModelsLoading && Boolean(currentDeviceSelected?.value)));
    const [allCurrentClustersOptions, setAllCurrentClustersOptions] = useState([]);

    const [modeledDeviceSelected, setModeledDeviceSelected] = useState(null);

    const [trendData, { isLoading: isTrendDataLoading }] = useDataWithRoute(`/api/v6/report/virtual_cluster_trend_proc?client_id=${client.clientId}&period_id=${periodId}&vcenter_name=${encodeURI(searchParams.get('current_device'))}&cluster_name=${encodeURI(searchParams.get('current_cluster'))}`, (!isAllSavedModelsLoading && Boolean(currentDeviceSelected?.value && currentClusterSelected?.value)));

    const [cloudMigrationData, { isLoading: isCloudMigrationDataLoading }] = useDataWithRoute(`/api/v6/report/virtual_cloud_migration_proc?client_id=${client.clientId}&period_id=${periodId}&resource_group=${encodeURI(modeledDeviceSelected?.value)}`, (!isAllSavedModelsLoading && Boolean(modeledDeviceSelected?.value)));

    const [{ recordsets: cloudResourceGroupHistory} , { isLoading: isResourceGroupHistoryLoading }] = useDataWithRoute(`/api/v6/report/cloud_history_modeling_by_resource_group_proc?client_id=${client.clientId}&period_id=${periodId}&resource_group=${encodeURI(modeledDeviceSelected?.value)}`, (!isAllSavedModelsLoading && Boolean(modeledDeviceSelected?.value)));

    const [currentPerfTrendData, setCurrentPerfTrendData] = useState([]);
    const [modeledPerfTrendData, setModeledPerfTrendData] = useState([]);

    const [perfConfig, setPerfConfig] = useState({});

    const titleDivRef = useRef(null);

    const [clusterCost, setClusterCost] = useState(0);

    const [chartType, setChartType] = useState(
      searchParams.get("chart_type") || "vcpu"
    );

    const isLoading = isAllDevicesWithClustersLoading || isAllDevicesLoading || isAllCurrentClustersLoading || isAllVirtualDevicesLoading || isAllSavedModelsLoading || isAllResourceGroupsLoading || isResourceGroupHistoryLoading;

    const onChartTypeChange = (type) => {
      setChartType(type.name);
      searchParams.set("chart_type", type.name);
      setSearchParams(searchParams, { replace: true });
    };

    const setCurrentDevicesFormat = (currentDeviceParam) => {
        const allDevicesOptionsNew = (allDevicesWithClusters?.recordset?.map?.(device => ({
            ...device,
            value: device.datacenter_name || device.device_name,
            label: device.datacenter_name || device.device_name,
        })) ?? []).filter((value, index, self) =>
                index === self.findIndex((t) => (
                    t.value === value.value
                ))
        );

        let currentDeviceFound = null;

        if (currentDeviceParam) {
            currentDeviceFound = allDevicesOptionsNew?.find?.(device => device.value === currentDeviceParam) ?? currentDeviceFound;
        }

        setAllDevicesWithClustersOptions(allDevicesOptionsNew);

        if (currentDeviceFound) {
            setCurrentDeviceSelected(currentDeviceFound);
        }
    }

    const setModeledDevicesFormat = (modeledDeviceParam) => {
        const allDevicesOptionsNew = ([...(allResourceGroups?.recordset ?? [])]?.map?.(device => ({
            value: device.device_name,
            label: device.device_name,
        })) ?? [])?.filter((value, index, self) =>
                index === self.findIndex((t) => (
                    t.value === value.value
                ))
        );

        let modeledDeviceFound = null;

        if (modeledDeviceParam) {
            modeledDeviceFound = allDevicesOptionsNew?.find?.(device => device.value === modeledDeviceParam) ?? modeledDeviceFound;
        }

        setAllDevicesOptions(allDevicesOptionsNew);
        if (modeledDeviceFound) {
            setModeledDeviceSelected(modeledDeviceFound);
        }
    }
    
    const calculateHighestResource = (clusterData) => {
        if (clusterData.length === 0) {
            return null;
        }
      
        let mostRecentDatum = clusterData[clusterData.length-1]
        let resources = {
            pct_cpu_util: mostRecentDatum['pct_cpu_util'],
            pct_disk_util: mostRecentDatum['pct_disk_util'],
            pct_memory_util: mostRecentDatum['pct_memory_util']
        }
        let maxResourceKey = Object.entries(resources).reduce((maxKey, [key, value]) => {
            return value > resources[maxKey] ? key : maxKey;
        }, Object.keys(resources)[0]);
  
        return maxResourceKey
    };

    const setCurrentClusterFormat = (currentClusterParam) => {
        const allCurrentClusterOptions = allCurrentClusters?.recordsets?.[0]?.map?.(cluster => ({
            ...cluster,
            value: cluster.cluster_name,
            label: cluster.cluster_name,
        })) ?? [];

        let currentClusterFound = null;

        if (currentClusterParam) {
            currentClusterFound = allCurrentClusterOptions?.find?.(cluster => cluster.value === currentClusterParam);
        }

        setAllCurrentClustersOptions(allCurrentClusterOptions);
        if (currentClusterFound) {
            setCurrentClusterSelected(currentClusterFound);
        }
    }

    const setSavedModelFormat = () => {
        const foundModel = searchParams.get('model_name');
        const allOptions = allSavedModels.map(model => ({
            ...model,
            label: model.projectName,
            value: model.projectName,
        }));

        setAllSavedModelsOptions(allOptions);
        if (foundModel) {
            setSelectedModel(allOptions.find(model => model.label = decodeURIComponent(foundModel)));
        }
    }

    const setAllChartData = () => {
        let data = [...(trendData?.recordset ?? [])]?.sort?.((a, b) => Date.parse(a.collection_date) - Date.parse(b.collection_date));
    
        // Find the highest cost resource
        let costResourceType = calculateHighestResource(data);

        const sortedData = data.map(datum => {
            datum['total_cost'] = clusterCost * (datum[costResourceType] / 100)
            datum['utilization_cost'] = clusterCost
            return datum
        })

        const newCurrentPerfTrend = formatStockChartConfig(
            sortedData,
            allTypes.find((type) => type.name === chartType)?.chartDefs || []
        );

        let selectedType = allTypes.filter(type => type.name === chartType)

        let fieldsArr = selectedType[0].chartDefs.map(def => def.field);
        let growthRateField = selectedType[0].name === 'vcpu' ? 'cpu_growth_rate' : selectedType[0].name + '_growth_rate';
        let growthRate = cloudMigrationData?.recordset?.[0]?.[growthRateField]
        
        const modeledTrend = forecastWithRate(sortedData, fieldsArr, 7, growthRate).slice(sortedData.length).map(datum => ({
            ...datum,
            perf_threshold: (modeledDeviceSelected?.perf_threshold ?? 0) - datum.iops,
        }));

        const sortedDataForForecast = modeledTrend.map(datum => {
            datum['total_cost'] = clusterCost * (datum[costResourceType] / 100);
            datum['utilization_cost'] = clusterCost;
            return datum;
        });

        const resourceGroupHistory = cloudResourceGroupHistory?.[0] || [];

        const modeledResourceGroupTrend = forecastWithRateV2(cloudResourceGroupHistory?.[0] || [], ["vcpu", "disk_used", "memory_used",], 7, growthRate);

        const allModeledResourceGroupTrend = [...resourceGroupHistory, ...modeledResourceGroupTrend];

        const allResourceGroupExistingDataObject = {};
        allModeledResourceGroupTrend.forEach(datum => {
            const monthDate = dayjs(datum.collection_date).format("MM/YYYY");
            if (!allResourceGroupExistingDataObject.hasOwnProperty(monthDate)) {
                allResourceGroupExistingDataObject[monthDate] = {...datum};
            }
        });

        let min = 0;
        let max = 0;

        const modeledForecastWithResourceForecast = sortedDataForForecast.map(datum => {
            const monthDate = dayjs(datum.collection_date).format("MM/YYYY");
            const newDatum = {...datum};
            const existingRGForecastDatum = allResourceGroupExistingDataObject[monthDate];
            if (existingRGForecastDatum) {
                newDatum.used = newDatum.used + existingRGForecastDatum.disk_used;
                newDatum.total_virtual_cpus_used = newDatum.total_virtual_cpus_used + existingRGForecastDatum.vcpu;
                newDatum.memory_used = newDatum.memory_used + existingRGForecastDatum.memory_used;
            }

            // handling Y axis min/maxes
            const nums = fieldsArr.map(field => newDatum[field]);
            const thisMin = Math.min(...nums);
            const thisMax = Math.max(...nums);

            if (!min || thisMin < min) {
                min = thisMin;
            }
            if (!max || thisMax > max) {
                max = thisMax;
            }

            return newDatum;
        });

        const newModeledPerfTrend = formatStockChartConfig(modeledForecastWithResourceForecast, allTypes.find((type) => type.name === chartType)?.chartDefs || []);

        setCurrentPerfTrendData(newCurrentPerfTrend.series);
        setModeledPerfTrendData(newModeledPerfTrend.series.map(series => ({
            ...series,
            zoneAxis: "x",
            zones: [
              {
                value: Date.parse(
                  sortedData?.[sortedData.length - 1]?.collection_date?.split?.("T")?.[0]
                ),
              },
              {
                dashStyle: "Dash",
              },
            ],
        })));

        let yAxis = allTypes.find((type) => type.name === chartType)?.yAxis || [];
        let colors = allTypes.find((type) => type.name === chartType)?.colors || [];

        setPerfConfig({
            yAxis: {
                ...yAxis,
                min,
                max,
            },
            colors
        } || {});
    }

    useEffect(() => {
        setCurrentDevicesFormat(searchParams.get('current_device'));
        setModeledDevicesFormat(searchParams.get('modeled_device'));
    }, [allDevices, allDevicesWithClusters, allResourceGroups]);

    useEffect(() => {
        setCurrentClusterFormat(searchParams.get('current_cluster'));
    }, [allCurrentClusters]);

    useEffect(() => {
        setSavedModelFormat();
    }, [allSavedModels]);

    useEffect(() => {
        if (selectedModel?.currentCluster && decodeURIComponent(searchParams.get('current_cluster')) !== selectedModel?.currentCluster) {
            setCurrentClusterSelected(allCurrentClustersOptions.find(cluster => cluster.value === selectedModel.currentCluster));
        }
    }, [allCurrentClustersOptions, selectedModel]);

    useEffect(() => {
        if (!isLoading) {
            setAllChartData();
        }
    }, [trendData, currentDeviceSelected, modeledDeviceSelected, chartType, clusterCost, cloudResourceGroupHistory, isLoading]);

    useEffect(() => {
        if (currentDeviceSelected?.value && currentClusterSelected?.value && !modeledDeviceSelected?.value && allDevicesOptions.length) {
            setModeledDeviceSelected(allDevicesOptions[0]);
        }
    }, [currentClusterSelected, currentDeviceSelected, modeledDeviceSelected, allDevicesOptions]);

    const clearSelectedModel = () => {
        setSelectedModel(null);
        searchParams.delete('model_name');
        setSearchParams(searchParams);
    }


    const onCurrentDeviceChange = device => {
        setCurrentClusterSelected(null)
        clearSelectedModel();
        searchParams.delete('current_cluster');
        searchParams.delete('modeled_device');
        searchParams.set('current_device', encodeURIComponent(device.value));
        setSearchParams(searchParams, {replace: true});
        setCurrentDeviceSelected(device);
    }

    const onCurrentClusterChange = cluster => {
        clearSelectedModel();
        searchParams.set('current_cluster', encodeURIComponent(cluster.value));
        setSearchParams(searchParams, {replace: true});
        setCurrentClusterSelected(cluster);
    }

    const onModeledDeviceChange = device => {
        clearSelectedModel();
        searchParams.set('modeled_device', encodeURIComponent(device.value));
        setSearchParams(searchParams, {replace: true});
        setModeledDeviceSelected(device);
    }

    const onModelCreateOption = val => {
        const newOption = {
            label: val,
            value: val,
        };
        setAllSavedModelsOptions(prev => [...prev, newOption]);
        setSelectedModel(newOption);
    }

    const onModelChange = model => {
        clearSelectedModel()
        searchParams.set('model_name', encodeURIComponent(model?.label));
        setSelectedModel(model);
        searchParams.set('current_device', encodeURIComponent(model?.currentDevice));
        setCurrentDeviceSelected(allDevicesWithClustersOptions.find(device => device.label === model.currentDevice));
        searchParams.set('current_cluster', encodeURIComponent(model?.currentCluster));
        setCurrentClusterSelected({
            label: model.currentCluster,
            value: model.currentCluster
        });
        setModeledDeviceSelected(allDevicesOptions.find(device => device.label === model.modeledDevice));
        setSearchParams(searchParams);
    }

    const determineModelName = () => {
        if (selectedModel?.label) return selectedModel.label;
        else {
            let label = `${dayjs().format('MM/DD/YY')} Model`;
            let duplicate = allSavedModelsOptions.find(model => model.label === label);
            if (duplicate) {
                const allLikeModels = [...allSavedModelsOptions].filter(model => model?.label?.split('(')?.[0] === label);
                const allIterations = allLikeModels.map(model => {
                    let num = model?.label?.split('(')?.[1]?.replace?.(')', '');
                    return parseInt(num);
                }).sort((a, b) => a - b);
                const num = allIterations[allIterations.length - 1] + 1;

                label += `(${isNaN(num) ? 1 : num})`
            }
            return label;
        }
    }

    const onSaveClick = async () => {
        try {
            const plan = {
                clientId: client.clientId,
                notes: '',
                projectName: determineModelName(),
                currentDevice: currentDeviceSelected.label,
                currentCluster: currentClusterSelected.label,
                modeledDevice: modeledDeviceSelected.label,
            };
            const token = await getAccessTokenSilently();
            if (!selectedModel?._id) {
                const postData = await PostAuth(`/api/v6/admin/cluster_device_migration_modeling`, plan, token);
                setAllSavedModelsOptions([
                    ...allSavedModelsOptions,
                    {
                        label: plan.projectName,
                        value: plan.projectName
                    }
                ])
                setSelectedModel({
                    ...postData?.data,
                    label: plan.projectName,
                    value: plan.projectName,
                });
            } else {
                const putData = await PutAuth(`/api/v6/admin/cluster_device_migration_modeling/${selectedModel._id}`, plan, token);
                setSelectedModel({
                    ...putData?.data,
                    label: plan.projectName,
                    value: plan.projectName,
                });
            }
        } catch (error) {
            console.error(error);
        }
    }

    const onDeleteClick = async () => {
        const planId = selectedModel?._id;
        const token = await getAccessTokenSilently();
        if (planId) {
            try {
                await DeleteAuth(`/api/v6/admin/cluster_device_migration_modeling/${planId}`, token);
                clearSelectedModel()
                reFetchModels();
            } catch (error) {
                console.error(error);
            }
        }
    }

    const isGlobalHistoryLoading = isTrendDataLoading && currentClusterSelected?.value && currentDeviceSelected?.value;

    return (<>
        <Card className={`report-card p-2 nav-card-${theme} mb-2`}>
            <div className={"d-flex justify-content-between"}>
                <VsiH4>Cluster Migration Modeling</VsiH4>
            </div>
            <VsiBubble noAutobot>
                <div className={'d-flex flex-row align-items-end'}>
                    <VsiCreatableSelect options={allSavedModelsOptions}
                                        isDisabled={isAllSavedModelsLoading}
                                        isLoading={isAllSavedModelsLoading}
                                        label={'Saved/save new model:'}
                                        minWidth={350}
                                        placeholder={'Type a project name and press enter'}
                                        onChange={onModelChange}
                                        onCreateOption={onModelCreateOption}
                                        defaultValue={selectedModel}/>
                    <ButtonGroup className={"mb-2"}>
                        <Button color={'danger'}
                                disabled={(!selectedModel?._id || isAllDevicesLoading || isAllSavedModelsLoading || isAllCurrentClustersLoading || isAllDevicesWithClustersLoading)}
                                onClick={onDeleteClick}>
                            <i className="fa-solid fa-trash me-2"/>
                            <span>Delete</span>
                        </Button>
                        <Button color={'success'} onClick={onSaveClick}>
                            <i className="fa-solid fa-floppy-disk me-2"/>
                            <span>Save</span>
                        </Button>
                    </ButtonGroup>
                </div>
            </VsiBubble>
        </Card>
        <Row>
            <Col xs={12} lg={6}>
                <Card className={`report-card p-2 nav-card-${theme}`}>
                    <div className={"d-flex justify-content-between"}
                         style={{minHeight: titleDivRef?.current?.scrollHeight ?? null}}>
                        <VsiH5 style={{fontStyle: 'italic'}}>Current History</VsiH5>
                    </div>
                    <VsiBubble noAutobot>
                        <div className={"d-flex"}>
                            <VsiLabeledSelect options={allDevicesWithClustersOptions}
                                              isDisabled={isAllDevicesWithClustersLoading || isGlobalHistoryLoading}
                                              isLoading={isAllDevicesWithClustersLoading || isGlobalHistoryLoading}
                                              label={'Source Device:'}
                                              minWidth={'50%'}
                                              onChange={onCurrentDeviceChange}
                                              defaultValue={currentDeviceSelected}/>
                            <VsiLabeledSelect options={allCurrentClustersOptions}
                                              isDisabled={(allCurrentClustersOptions.length < 1 || isAllCurrentClustersLoading) || isGlobalHistoryLoading}
                                              isLoading={(currentDeviceSelected?.value && isAllCurrentClustersLoading) || isGlobalHistoryLoading}
                                              label={'Cluster:'}
                                              placeholder={currentDeviceSelected ? 'Select...' : 'Please select source device'}
                                              minWidth={'50%'}
                                              onChange={onCurrentClusterChange}
                                              defaultValue={currentClusterSelected}/>
                        </div>
                    </VsiBubble>
                    <Row>
                        <Col xs={12}>
                            <VsiBubble noAutobot>
                                <Row>
                                    {chartType === "cost" ? (<>
                                        <Col>
                                            <VsiSmallText>This is an estimate.</VsiSmallText>
                                        </Col>
                                        <Col xs={4}>
                                            <FormGroup>
                                                <Row>
                                                    <Col sm="7">
                                                        <Label for="clusterCost">Cost per cluster:</Label>
                                                    </Col>
                                                    <Col sm="5">
                                                        <Input
                                                            id="clusterCost"
                                                            name="clusterCost"
                                                            type="text"
                                                            value={clusterCost}
                                                            onChange={(e) => {
                                                                setClusterCost(e.target.value);
                                                            }}
                                                        />
                                                    </Col>
                                                </Row>
                                            </FormGroup>
                                        </Col>
                                    </>) : null}
                                    <Col className={"d-flex flex-row justify-content-end"}>
                                        <ButtonGroup>
                                        {allTypes.map((type, index) => (
                                            <Button
                                                disabled={chartType === type.name}
                                                key={`chart-btn-${index}-${type.name}`}
                                                onClick={() => onChartTypeChange(type)}
                                            >
                                                {type.label}
                                            </Button>
                                        ))}
                                        </ButtonGroup>
                                    </Col>
                                </Row>
                                <Stockchart height={450}
                                            isLoading={isLoading}
                                            title={(currentDeviceSelected?.value && currentClusterSelected?.value) ? `${currentClusterSelected.label} performance on ${currentDeviceSelected.label}` : ''}
                                            series={currentPerfTrendData}
                                            config={perfConfig}/>
                            </VsiBubble>
                        </Col>
                    </Row>
                </Card>
            </Col>

            <Col xs={12} lg={6}>
                <Card className={`report-card p-2 nav-card-${theme} h-100`}>
                {currentDeviceSelected?.value && currentClusterSelected?.value ? (
                (<>
                    <div className={"d-flex justify-content-between"} ref={titleDivRef}>
                        <VsiH5 style={{fontStyle: 'italic'}}>Modeled Forecast</VsiH5>
                    </div>
                    <VsiBubble noAutobot>
                        <div className={"d-flex justify-content-between align-items-center"}>
                            <VsiLabeledSelect options={allDevicesOptions}
                                              isDisabled={isAllDevicesLoading || isGlobalHistoryLoading}
                                              isLoading={isAllDevicesLoading || isGlobalHistoryLoading}
                                              label={'Target Device:'}
                                              minWidth={'50%'}
                                              onChange={onModeledDeviceChange}
                                              defaultValue={modeledDeviceSelected}/>
                        </div>
                    </VsiBubble>
                    <Row>
                        <Col xs={12}>
                            <VsiBubble noAutobot>
                                <Row>
                                    {chartType === "cost" ? (
                                        <Col>
                                            <VsiSmallText>This is an estimate.</VsiSmallText>
                                        </Col>
                                    ) : null}
                                    <Col className={"d-flex flex-row justify-content-end"}>
                                        <ButtonGroup>
                                        {allTypes.map((type, index) => (
                                            <Button
                                                disabled={chartType === type.name}
                                                key={`chart-btn-${index}-${type.name}`}
                                                onClick={() => onChartTypeChange(type)}
                                            >
                                                {type.label}
                                            </Button>
                                        ))}
                                        </ButtonGroup>
                                    </Col>
                                </Row>
                                <Stockchart height={450}
                                            isLoading={isLoading}
                                            title={(modeledDeviceSelected?.value && currentClusterSelected?.value) ? `${currentClusterSelected.label} performance on ${modeledDeviceSelected.label}` : ''}
                                            series={isTrendDataLoading ? [] : modeledPerfTrendData}
                                            config={perfConfig}/>
                            </VsiBubble>
                        </Col>
                        {isCloudMigrationDataLoading ? <Spinner className={'ms-3'}/> : 
                            <Col xs={12} className={"mb-1"}>
                                <SidePerformanceCategories cloud={cloudMigrationData?.recordset?.[0]}
                                                        theme={theme}/>
                            </Col>
                        }
                    </Row>
                </>)
            ) :
                <>
                    <VsiH5 style={{fontStyle: 'italic'}}>
                        * Please select a source device and cluster.
                    </VsiH5>
                </>}
                </Card>
            </Col>
        </Row>
    </>)
}
